import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../components/Breadcrumbs';
import './Privacy.css';

function Privacy() {
    return (
        <>
            <Helmet>
                <title>Privacy Policy - NeuAIs Data Protection & Security</title>
                <meta name="description" content="NeuAIs Privacy Policy: Learn how we protect your data, handle personal information, and ensure security in our GPU cloud platform. GDPR & CCPA compliant." />
                <meta name="keywords" content="privacy policy, data protection, GDPR, CCPA, data security, cloud privacy" />
                <link rel="canonical" href="https://lab.neuais.com/privacy" />
            </Helmet>

            <Breadcrumbs />

            <div className="info-page">
                <div className="info-container">
                    <h1>Privacy Policy</h1>
                    <div className="info-content">
                        <section>
                            <p><em>Last updated: December 13, 2024</em></p>
                            <p>
                                At NeuAIs, we take your privacy seriously. This policy explains how we collect, use,
                                and protect your personal information.
                            </p>
                        </section>

                        <section>
                            <h2>Information We Collect</h2>
                            <h3>Account Information</h3>
                            <ul>
                                <li>Email address and name (for account creation)</li>
                                <li>Organization details (for enterprise accounts)</li>
                                <li>Payment information (processed securely via Stripe)</li>
                            </ul>

                            <h3>Usage Data</h3>
                            <ul>
                                <li>GPU usage metrics and session duration</li>
                                <li>Notebook activity and code execution logs</li>
                                <li>System performance and error diagnostics</li>
                                <li>IP addresses and browser information</li>
                            </ul>

                            <h3>Your Content</h3>
                            <ul>
                                <li>Code, notebooks, and datasets you create</li>
                                <li>Model checkpoints and training outputs</li>
                                <li>Collaboration session data</li>
                            </ul>
                        </section>

                        <section>
                            <h2>How We Use Your Information</h2>
                            <ul>
                                <li><strong>Service Delivery:</strong> Provide GPU computing and manage your sessions</li>
                                <li><strong>Platform Improvement:</strong> Optimize performance and add new features</li>
                                <li><strong>Support:</strong> Respond to inquiries and technical issues</li>
                                <li><strong>Security:</strong> Detect fraud, prevent abuse, ensure compliance</li>
                                <li><strong>Communications:</strong> Send important updates, maintenance notices</li>
                                <li><strong>Legal Compliance:</strong> Meet regulatory and legal obligations</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Data Security & Protection</h2>
                            <p><strong>Infrastructure Security:</strong></p>
                            <ul>
                                <li>All data encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                                <li>Isolated Docker containers for each session</li>
                                <li>SOC 2 Type II compliant infrastructure</li>
                                <li>Regular security audits and penetration testing</li>
                            </ul>

                            <p><strong>Access Controls:</strong></p>
                            <ul>
                                <li>Multi-factor authentication (MFA) available</li>
                                <li>Role-based access control (RBAC) for teams</li>
                                <li>Session auto-logout after inactivity</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Data Retention</h2>
                            <ul>
                                <li><strong>Active Sessions:</strong> Stored for session duration + 7 days</li>
                                <li><strong>Account Data:</strong> Retained while account is active + 90 days post-deletion</li>
                                <li><strong>Usage Logs:</strong> Kept for 12 months for billing and security</li>
                                <li><strong>Backup Data:</strong> Deleted within 30 days of account termination</li>
                            </ul>
                        </section>

                        <section>
                            <h2>Your Rights (GDPR & CCPA)</h2>
                            <ul>
                                <li><strong>Access:</strong> Request a copy of your personal data</li>
                                <li><strong>Rectification:</strong> Correct inaccurate information</li>
                                <li><strong>Deletion:</strong> Request account and data removal</li>
                                <li><strong>Portability:</strong> Export your data in standard formats</li>
                                <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
                            </ul>
                            <p>To exercise these rights, email <a href="mailto:privacy@neuais.com">privacy@neuais.com</a></p>
                        </section>

                        <section>
                            <h2>Third-Party Services</h2>
                            <ul>
                                <li><strong>Payment Processing:</strong> Stripe (PCI-DSS compliant)</li>
                                <li><strong>Analytics:</strong> Self-hosted Plausible (privacy-friendly)</li>
                                <li><strong>Infrastructure:</strong> AWS, GCP (data residency options available)</li>
                            </ul>
                            <p>We do not sell your personal information to third parties.</p>
                        </section>

                        <section>
                            <h2>Cookies & Tracking</h2>
                            <p>We use essential cookies for:</p>
                            <ul>
                                <li>Session management and authentication</li>
                                <li>User preferences and settings</li>
                                <li>Security and fraud prevention</li>
                            </ul>
                            <p>We do not use advertising or tracking cookies.</p>
                        </section>

                        <section>
                            <h2>Contact & Updates</h2>
                            <p>
                                Questions about privacy? Email <a href="mailto:privacy@neuais.com">privacy@neuais.com</a> or
                                review our <Link to="/terms">Terms of Service</Link>.
                            </p>
                            <p>We'll notify you of significant policy changes via email 30 days in advance.</p>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Privacy;
